/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { Box, Spacer, Text } from "@nordcloud/gnui";
import { FlexContainer, QueryPagination, showSuccess } from "~/components";
import { CommentCreate } from "~/components/Comment/CommentCreate";
import { useShowError } from "~/components/Error/useShowError";
import { InlineComments } from "~/components/InlineEdit/InlineComment";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { Action } from "~/constants/actions";
import { RouteParams } from "~/models";
import { Comment, SubmitComment } from "~/models/comments";
import { generateActionSuccessText, useAxios, useQueryState } from "~/tools";

type CommentType = {
  totalCount: number;
  comments: Comment[];
};

const defaultData = {
  totalCount: 0,
  comments: [],
};

function Comments() {
  const state = useParams<RouteParams>();
  const { customerId } = state;
  const { Axios } = useAxios();
  const { showError } = useShowError();
  const [commentsData, setCommentsData] = React.useState<CommentType>(
    defaultData
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [{ limit, page }] = useQueryState();

  const fetchComments = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await Axios().get(
        `/customers/${customerId}/comments?limit=${limit}&page=${page}`
      );
      setCommentsData(() => ({
        comments: data.comments,
        totalCount: data.totalCount,
      }));
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, limit, page, showError]);

  React.useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const onSubmit = async (values: SubmitComment, type: string) => {
    try {
      switch (type) {
        case Action.CREATE:
          await Axios().post(`/customers/${customerId}/comments`, {
            text: values.text,
          });
          showSuccess(generateActionSuccessText(`Comment`)()("posted")());
          break;
        case Action.EDIT:
          await Axios().put(`/customers/${customerId}/comments/${values.id}`, {
            text: values.text,
          });
          showSuccess(generateActionSuccessText(`Comment`)()("updated")());
          break;
        case Action.REMOVE:
          await Axios().delete(
            `/customers/${customerId}/comments/${values.id}`
          );
          showSuccess(generateActionSuccessText(`Comment`)()("deleted")());
          break;
        default:
          return;
      }
    } catch (e) {
      showError(e.response);
    } finally {
      await fetchComments();
    }
  };

  return (
    <Box>
      <FlexContainer justify="center">
        <CommentCreate onSubmit={onSubmit} />
      </FlexContainer>
      <Spacer height="3rem" />

      <LoaderWrap loading={isLoading}>
        {commentsData.comments.length ? (
          <>
            {commentsData.comments.map((com: Comment) => (
              <InlineComments
                key={com.id}
                item={com}
                property="text"
                onSubmit={onSubmit}
              />
            ))}
            <QueryPagination count={Number(commentsData?.totalCount) || 0} />
          </>
        ) : (
          <Text>No comments yet.</Text>
        )}
      </LoaderWrap>
    </Box>
  );
}

export { Comments };
